import {
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Alert, Box, Chip, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActivityLogModelType } from '../../models/ActivityLog';
import { getModelLabel } from '../../utils/ModelIconMapping';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';
import { StyledTypography } from '../styles/styles';
import ActivityLogEmptyState from './ActivityLogEmptyState';
import ActivityLogPageEntry from './ActivityLogPageEntry';
import ModelActivityLogPageFilterPanel from './ModelActivityLogPageFilterPanel';
import { getModelNavigationPath } from './contentVersionViewerUtils';
import { useModelActivityLogPageList } from './useModelActivityLogPageList';

interface ModelActivityLogPageProps {
  tone?: keyof typeof Palette;
}

const getHumanReadableModelType = (modelType: string): string =>
  _.capitalize(getModelLabel(modelType));

interface ModelActivityLogPageContentProps extends ModelActivityLogPageProps {
  modelType: ActivityLogModelType;
  modelId: string;
}

const ModelActivityLogPageContent: React.FC<ModelActivityLogPageContentProps> = ({
  tone = 'default',
  modelType,
  modelId,
}) => {
  const navigate = useNavigate();
  const {
    logs,
    loading,
    loadingMore,
    error,
    totalLogs,
    showFilterPanel,
    setShowFilterPanel,
    filters,
    scrollRootRef,
    lastLogElementRef,
    backendService,
    handleFilterChange,
    clearFilters,
    refreshLogs,
  } = useModelActivityLogPageList(modelType, modelId);

  return (
    <StyledSection tone={tone}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <StyledButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(getModelNavigationPath(modelType, modelId))}
            sx={{ mb: 2 }}
          >
            Back to {getHumanReadableModelType(modelType)}
          </StyledButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <StyledTypography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            {modelType} Activity Logs
            {totalLogs > 0 && (
              <Chip label={totalLogs} size="small" sx={{ ml: 1 }} color="primary" />
            )}
          </StyledTypography>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Toggle Filters">
              <IconButton
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                color={showFilterPanel ? 'primary' : 'default'}
                aria-label="Toggle filters"
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={refreshLogs} disabled={loading} aria-label="Refresh logs">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <ModelActivityLogPageFilterPanel
          showFilterPanel={showFilterPanel}
          filters={filters}
          tone={tone}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
        />
      </Box>

      <Box ref={scrollRootRef} sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <ActivityLogEmptyState subtitle="There are no activity logs available for this model." />
        ) : (
          <Box>
            {logs.map((log, index) => (
              <div
                key={log.id as string}
                ref={index === logs.length - 1 ? lastLogElementRef : undefined}
              >
                <ActivityLogPageEntry
                  log={log}
                  tone={tone}
                  allLogs={logs}
                  backendService={backendService}
                />
              </div>
            ))}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </StyledSection>
  );
};

const ModelActivityLogPage: React.FC<ModelActivityLogPageProps> = ({ tone = 'default' }) => {
  const { modelType, modelId } = useParams<{
    modelType: ActivityLogModelType;
    modelId: string;
  }>();

  if (!modelType || !modelId) {
    return (
      <StyledSection tone={tone}>
        <Alert severity="error">Invalid model type or ID</Alert>
      </StyledSection>
    );
  }

  return <ModelActivityLogPageContent tone={tone} modelType={modelType} modelId={modelId} />;
};

export default ModelActivityLogPage;
